import { Fragment, PureComponent, ReactNode } from 'react';
import {
    components,
    OptionProps,
    ValueContainerProps,
    ControlProps,
    MenuProps,
    StylesConfig,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { CSSObject } from '@emotion/serialize';
import { ONE_CHAR_GENDER } from '../pages/NewPatient';
import { getAllPatientsPhoneStartsWith } from '../store/idbHandler';
import { Patient } from '../store/types/patient';
import fetchProfilesByMobile, { ProfileByMobile } from '../api/fetchProfilesByMobile';
import { AntTooltip } from './common/AntTooltip';
import { AbhaIconNotLinked, EkaCareIcon, infoIcon2 } from './common/Icons';
import { track } from '../tracking';
import PhoneCountryCode from './common/PhCountryCode';
import { CountryCode } from 'libphonenumber-js';
import { getAllCountryCodes } from '../modules/add-patient';

type SuggestionItem = Patient & { __isNew__?: boolean; __api__?: boolean };

const Option = (props: OptionProps<SuggestionItem, false>) => {
    const { data, selectProps } = props;
    const { onCountryCodeChange } = selectProps;

    return (
        <components.Option {...props}>
            <div className="flex items-center justify-between font-600 text-neutral-3">
                {data.__isNew__ ? (
                    <p className="text-actionPrimary">
                        + Add another patient with {data.personal.phone.n}
                    </p>
                ) : (
                    <Fragment>
                        <div className="flex items-center w-75p">
                            {data.personal.onApp && (
                                <AntTooltip content="Connected on eka.care app">
                                    <span className="top-1 flex-shrink-0 right-1 relative mr-3 w-16 h-16">
                                        <EkaCareIcon />
                                    </span>
                                </AntTooltip>
                            )}
                            {(data?.abha?.id || data?.personal?.fromNdhmDiscovery) && (
                                <AntTooltip content="ABHA address is present">
                                    {AbhaIconNotLinked}
                                </AntTooltip>
                            )}
                            {data.personal.fromBusiness && (
                                <AntTooltip content="From business directory">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="1em"
                                        viewBox="0 0 576 512"
                                        className="dr-min-w-16 flex-shrink-0 dr-w-16 dr-h-16 mr-4"
                                    >
                                        <path d="M272.5 5.7c9-7.6 22.1-7.6 31.1 0L464 141.9V56c0-13.3 10.7-24 24-24s24 10.7 24 24V182.6l55.5 47.1c10.1 8.6 11.3 23.7 2.8 33.8s-23.7 11.3-33.8 2.8L512 245.5V432c0 44.2-35.8 80-80 80H144c-44.2 0-80-35.8-80-80V245.5L39.5 266.3c-10.1 8.6-25.3 7.3-33.8-2.8s-7.3-25.3 2.8-33.8l264-224zM112 204.8V432c0 17.7 14.3 32 32 32H432c17.7 0 32-14.3 32-32V204.8L288 55.5 112 204.8zM272 192h32c8.8 0 16 7.2 16 16v48h48c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H320v48c0 8.8-7.2 16-16 16H272c-8.8 0-16-7.2-16-16V320H208c-8.8 0-16-7.2-16-16V272c0-8.8 7.2-16 16-16h48V208c0-8.8 7.2-16 16-16z"></path>
                                    </svg>
                                </AntTooltip>
                            )}
                            <p>
                                {onCountryCodeChange && data.personal.phone.c
                                    ? `${data.personal.phone.c} `
                                    : null}
                                {data.personal.phone.n}
                                {`, ${data.personal.name.f} ${
                                    data.personal.name.l ? data.personal.name.l : ''
                                }`}
                            </p>
                        </div>
                        <div>
                            {ONE_CHAR_GENDER[data.personal.gender.toLowerCase()]}
                            {data.personal.age?.value ? `, ${data.personal.age?.value} Y` : ''}
                        </div>
                    </Fragment>
                )}
            </div>
            {(data.personal?.['health-ids']?.[0] || data?.personal?.abha?.id) && (
                <span className="text-10 flex space-x-4">
                    <svg
                        className="flex-shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M7.2027 16.3251C5.82059 16.3251 4.66426 15.8563 4.01172 15.2155C4.66426 14.5747 5.82059 14.106 7.2027 14.106C8.58479 14.106 9.74113 14.5747 10.3937 15.2155C9.74113 15.8563 8.58479 16.3251 7.2027 16.3251Z"
                            fill="#19A66A"
                            stroke="#19A66A"
                            stroke-width="0.97866"
                        />
                        <path
                            d="M16.9563 16.3251C15.6377 16.3251 14.534 15.857 13.9128 15.2155C14.534 14.574 15.6377 14.106 16.9563 14.106C18.2751 14.106 19.3788 14.574 20 15.2155C19.3788 15.857 18.2751 16.3251 16.9563 16.3251Z"
                            fill="#19A66A"
                            stroke="#19A66A"
                            stroke-width="0.97866"
                        />
                        <path
                            d="M7.8238 12.0666C6.79221 11.035 6.28076 9.8187 6.27979 8.85254C7.24596 8.85352 8.46228 9.36495 9.49389 10.3966C10.5255 11.4281 11.0369 12.6445 11.0379 13.6106C10.0717 13.6097 8.8554 13.0982 7.8238 12.0666Z"
                            fill="#19A66A"
                            stroke="#19A66A"
                            stroke-width="0.97866"
                        />
                        <path
                            d="M14.8604 10.3995C15.8921 9.36788 17.1084 8.85645 18.0745 8.85547C18.0735 9.82163 17.5621 11.038 16.5306 12.0696C15.4989 13.1011 14.2826 13.6126 13.3164 13.6136C13.3174 12.6474 13.8288 11.4311 14.8604 10.3995Z"
                            fill="#19A66A"
                            stroke="#19A66A"
                            stroke-width="0.97866"
                        />
                        <path
                            d="M11.2097 8.33807C11.2097 6.89337 11.6788 5.68435 12.3193 5C12.9597 5.68435 13.4288 6.89337 13.4288 8.33807C13.4288 9.78277 12.9597 10.9918 12.3193 11.6761C11.6788 10.9918 11.2097 9.78277 11.2097 8.33807Z"
                            fill="#19A66A"
                            stroke="#19A66A"
                            stroke-width="0.97866"
                        />
                        <path
                            d="M12.1596 18.8816C12.8607 18.8816 13.429 18.3133 13.429 17.6122C13.429 16.9111 12.8607 16.3428 12.1596 16.3428C11.4585 16.3428 10.8901 16.9111 10.8901 17.6122C10.8901 18.3133 11.4585 18.8816 12.1596 18.8816Z"
                            fill="#D74B41"
                            stroke="#D74B41"
                            stroke-width="0.97866"
                        />
                    </svg>{' '}
                    <span>{data.personal?.['health-ids']?.[0] || data?.personal?.abha?.id}</span>
                </span>
            )}
        </components.Option>
    );
};

const ValueContainer = (props: ValueContainerProps<Patient, false>) => {
    const { hasValue, children, getValue, setValue, selectProps } = props;
    const { isDisabled, isModal } = selectProps;
    if (hasValue) {
        const value: Patient = getValue()[0];

        return isModal ? (
            <div className={`relative flex items-center justify-between gap-8 py-10 px-12`}>
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        width="11"
                        height="15"
                        viewBox="0 0 11 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.5625 0.75C9.51953 0.75 10.3125 1.54297 10.3125 2.5V13C10.3125 13.9844 9.51953 14.75 8.5625 14.75H2.4375C1.45312 14.75 0.6875 13.9844 0.6875 13V2.5C0.6875 1.54297 1.45312 0.75 2.4375 0.75H8.5625ZM9 13V2.5C9 2.28125 8.78125 2.0625 8.5625 2.0625H2.4375C2.19141 2.0625 2 2.28125 2 2.5V13C2 13.2461 2.19141 13.4375 2.4375 13.4375H8.5625C8.78125 13.4375 9 13.2461 9 13ZM6.375 11.6875C6.59375 11.6875 6.8125 11.9062 6.8125 12.125C6.8125 12.3711 6.59375 12.5625 6.375 12.5625H4.625C4.37891 12.5625 4.1875 12.3711 4.1875 12.125C4.1875 11.9062 4.37891 11.6875 4.625 11.6875H6.375Z"
                            fill="#3E445C"
                        />
                    </svg>
                </div>
                <input
                    type="text"
                    value={value.personal.phone?.n}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full"
                    disabled={isDisabled}
                />
                <div className="mimic-box"></div>
            </div>
        ) : (
            <div className="add-pt-input">
                <input
                    type="text"
                    value={value.personal.phone?.n}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                    disabled={isDisabled}
                />
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        className="w-16 h-16 fill-neutral-5"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                    >
                        <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z"></path>
                    </svg>
                </div>
                <div className="mimic-box"></div>
            </div>
        );
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<Patient, false>) => {
    const { children } = props;
    return <Fragment>{children}</Fragment>;
};

const Menu = (props: MenuProps<Patient, false>) => {
    const { children, options } = props;

    if (options.length === 0) {
        return null;
    }

    return <components.Menu {...props}>{children}</components.Menu>;
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { selectProps, onChange, onFocus, value } = props;
    const {
        placeholder,
        onPhoneNumberBlur,
        isPhoneInputError,
        onInputChange,
        isDisabled,
        isModal,
        phCode,
    } = selectProps;

    const onCChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value && value.length > 0 && isNaN(parseInt(value))) {
            return;
        }
        e.target.value = value;
        onChange(e);
    };

    const onPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        const v = e.clipboardData.getData('text');
        e.preventDefault();
        e.stopPropagation();

        if (v && v.length >= 10) {
            let t = v.replace(/[^0-9]/g, '').replace('+91', '');
            if (t.length > 10) {
                t = t.substr(t.length - 10);
            }
            if (t.length === 10) {
                onInputChange(t);
            }
        }
    };

    return isModal ? (
        <div
            className={`relative flex items-center justify-between gap-8 py-10 px-12 ${
                isPhoneInputError ? 'error' : ''
            }`}
        >
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    width="11"
                    height="15"
                    viewBox="0 0 11 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M8.5625 0.75C9.51953 0.75 10.3125 1.54297 10.3125 2.5V13C10.3125 13.9844 9.51953 14.75 8.5625 14.75H2.4375C1.45312 14.75 0.6875 13.9844 0.6875 13V2.5C0.6875 1.54297 1.45312 0.75 2.4375 0.75H8.5625ZM9 13V2.5C9 2.28125 8.78125 2.0625 8.5625 2.0625H2.4375C2.19141 2.0625 2 2.28125 2 2.5V13C2 13.2461 2.19141 13.4375 2.4375 13.4375H8.5625C8.78125 13.4375 9 13.2461 9 13ZM6.375 11.6875C6.59375 11.6875 6.8125 11.9062 6.8125 12.125C6.8125 12.3711 6.59375 12.5625 6.375 12.5625H4.625C4.37891 12.5625 4.1875 12.3711 4.1875 12.125C4.1875 11.9062 4.37891 11.6875 4.625 11.6875H6.375Z"
                        fill="#3E445C"
                    />
                </svg>
            </div>
            <input
                type="text"
                className="focus:outline-none text-14 font-400 placeholder-neutral-6 w-full bg-darwin-neutral-50"
                pattern="\d+"
                placeholder={placeholder}
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onPhoneNumberBlur}
                onPaste={onPaste}
                disabled={isDisabled}
                autoFocus
                maxLength={phCode === '+91' ? 10 : undefined}
            />{' '}
            <div className="mimic-box"></div>
        </div>
    ) : (
        <div className={`add-pt-input ${isPhoneInputError ? 'error' : ''}`}>
            <input
                type="text"
                className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                pattern="\d+"
                placeholder={placeholder}
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onPhoneNumberBlur}
                onPaste={onPaste}
                disabled={isDisabled}
                maxLength={phCode === '+91' ? 10 : undefined}
                autoFocus
            />{' '}
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    className="w-16 h-16 fill-neutral-5"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z"></path>
                </svg>
            </div>
            <div className="mimic-box"></div>
        </div>
    );
};

const DropdownIndicator = () => null;
const NoOptionsMessage = () => null;
const LoadingIndicator = () => null;
const ClearIndicator = () => null;
const IndicatorsContainer = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Option,
    NoOptionsMessage,
    LoadingIndicator,
    Menu,
    ClearIndicator,
    IndicatorsContainer,
};

const customStyles: StylesConfig<Patient, false> = {
    menu: (base) => {
        return {
            ...base,
            paddingTop: 0,
            minWidth: 250,
            maxWidth: 250,
        } as CSSObject;
    },
};

type Props = {
    phCode?: string;
    phCodeEnum?: CountryCode;
    inputValue: string;
    onSelect: (p?: Patient) => void;
    onInputChange: (text: string) => void;
    onCountryCodeChange?: (code: string, countryCodeEnum: CountryCode) => void;
    disabled: boolean;
    suggestionsEnabled?: boolean;
    isModal?: boolean;
    onBlur?: any;
    className?: string;
    autoFocus?: boolean;
    setIsPhoneNumberError?: (val: boolean) => void;
    cid: string | undefined;
    isAbdmEnabled: boolean | undefined;
    fromNdhmDiscovery: boolean | undefined;
};

type State = {
    isSuggestedPtLoading: boolean;
    menuIsOpen: boolean;
};

const convertUserToLocal = (user: ProfileByMobile): SuggestionItem => {
    return {
        ...user,
        localId: user.id,
        __api__: true,
    };
};

class PatientPhoneAutoSuggest extends PureComponent<Props, State> {
    state: State = {
        isSuggestedPtLoading: false,
        menuIsOpen: false,
    };

    onPhoneNumberChange2 = (input: string): void => {
        this.setState({
            menuIsOpen: true,
        });

        const { disabled, onSelect } = this.props;

        if (disabled) {
            return;
        }

        this.props.onInputChange(input);

        if (this.props.fromNdhmDiscovery) {
            onSelect();
        }
    };

    onChange = (patient: SuggestionItem | null): void => {
        const { onSelect } = this.props;

        if (patient === null) {
            onSelect();
            return;
        }

        onSelect(patient);

        track('patient_selected', {
            isNew: patient.__isNew__ ? 'true' : 'false',
            isApi: patient.__api__ ? 'true' : 'false',
            fromNdhmDiscovery: String(patient.personal.fromNdhmDiscovery),
        });
    };

    onPhoneNumberBlur = (): void => {
        const { inputValue } = this.props;
        this.setState({ menuIsOpen: false });
        this.props.onBlur && this.props.onBlur(inputValue);
    };

    loadPatients = async (phone: string): Promise<SuggestionItem[]> => {
        if (!this.props.suggestionsEnabled) {
            return [];
        }

        const { cid } = this.props;

        const allPatients: Patient[] =
            (await getAllPatientsPhoneStartsWith(phone))?.filter((t) => !t.archived) || [];

        if (phone && phone.length === 10) {
            this.setState({ isSuggestedPtLoading: true });

            const res = await fetchProfilesByMobile(`+91${phone}`, cid, this.props.isAbdmEnabled);
            if (res.success && this.props.inputValue === phone) {
                const allPtSet = new Set(allPatients.map((t) => t.localId));
                res.profiles.forEach((p) => {
                    if (!allPtSet.has(p.id)) {
                        allPatients.push(convertUserToLocal(p));
                    }
                });
            }

            this.setState({ isSuggestedPtLoading: false });
        }

        if (phone && phone.length === 10 && allPatients.length > 0) {
            return [
                ...allPatients,
                {
                    __isNew__: true,
                    localId: new Date().getTime().toString(),
                    createdAt: new Date(),
                    personal: { name: { f: '', l: '' }, phone: { c: '', n: phone }, gender: '' },
                },
            ];
        }

        return allPatients;
    };

    private readonly filterOption = () => true;

    render(): ReactNode {
        const { menuIsOpen, isSuggestedPtLoading } = this.state;
        const {
            inputValue,
            disabled,
            autoFocus,
            phCode,
            onCountryCodeChange,
            isModal,
            phCodeEnum,
        } = this.props;

        const countryCodeData = getAllCountryCodes();

        const children = (
            <div className={`flex w-full ${isModal ? '' : 'gap-16'}`}>
                {onCountryCodeChange && (
                    <Fragment>
                        <div style={{ width: 80 }}>
                            <PhoneCountryCode
                                value={phCode}
                                disabled={disabled}
                                onSelect={onCountryCodeChange}
                                isModal={isModal}
                                countryCodeData={countryCodeData}
                                phoneNumber={inputValue}
                            />
                        </div>
                        {isModal && <div style={{ width: 8, backgroundColor: 'white' }} />}
                    </Fragment>
                )}

                <AsyncSelect
                    value={[]}
                    isClearable={true}
                    components={customComponents}
                    styles={customStyles}
                    loadOptions={this.loadPatients}
                    onInputChange={this.onPhoneNumberChange2}
                    inputValue={inputValue}
                    onChange={this.onChange}
                    onPhoneNumberBlur={this.onPhoneNumberBlur}
                    filterOption={this.filterOption}
                    menuIsOpen={menuIsOpen}
                    tabSelectsValue={false}
                    isDisabled={disabled}
                    placeholder={
                        this.props.isModal ? 'Enter Patient Phone Number' : 'e.g. 7740997399'
                    }
                    isModal={this.props.isModal}
                    className={this.props.isModal ? 'flex-1' : ''}
                    autoFocus={!!autoFocus}
                    phCode={phCode}
                    onCountryCodeChange={onCountryCodeChange}
                    isSuggestedPtLoading={isSuggestedPtLoading}
                />
                {isModal && (
                    <Fragment>
                        <div className="pl-8 bg-white flex justify-center items-center">
                            <AntTooltip content="Phone number is optional.">
                                <span>{infoIcon2('w-16 h-16')}</span>
                            </AntTooltip>
                        </div>
                    </Fragment>
                )}
            </div>
        );

        return this.props.isModal ? (
            <div className="space-y-4 flex w-full bg-darwin-neutral-50 rounded-4 justify-between flex-grow">
                {children}
            </div>
        ) : (
            <div className={`space-y-4 ${this.props.className ?? ''}`}>
                <div className="flex justify-between">
                    <div className="flex items-center gap-8">
                        <p className="text-neutral-3 text-14 font-700">Mobile Number</p>
                        <AntTooltip content="Phone number is optional.">
                            <span>{infoIcon2('w-16 h-16')}</span>
                        </AntTooltip>
                    </div>
                    {disabled && (
                        <span className="text-12 self-end mb-2 text-status-error select-none">
                            (can not be edited)
                        </span>
                    )}
                </div>
                {children}
            </div>
        );
    }
}

export default PatientPhoneAutoSuggest;

export const getErrorComp = (msg: string): ReactNode => {
    return (
        <div className="px-12 py-4 flex items-center space-x-4 bg-status-errorBG rounded-8">
            <div>
                <svg
                    className="w-14 h-14 fill-status-error"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM232 152C232 138.8 242.8 128 256 128s24 10.75 24 24v128c0 13.25-10.75 24-24 24S232 293.3 232 280V152zM256 400c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 385.9 273.4 400 256 400z"></path>
                </svg>
            </div>
            <p className="text-13 text-status-error">{msg}</p>
        </div>
    );
};
